/**
 * RefundWindowBanner — small amber notice shown at the top of a lesson
 * when the user is inside their 14-day refund window.
 *
 * Explains the daily lesson drip limit and how many unlocks remain
 * for the day. Also warns when they're about to void their refund
 * eligibility by crossing the consumption threshold.
 */

import React from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';

const RefundWindowBanner = ({ state, className = '' }) => {
  if (!state || !state.in_refund_window) return null;

  const remaining = Math.max(
    0,
    (state.daily_limit || 0) - (state.lessons_unlocked_today || 0),
  );
  const consumptionCloseToVoid = state.consumption_pct >= 40 && !state.refund_voided;

  return (
    <div
      data-testid="refund-window-banner"
      className={`mb-4 p-4 rounded-2xl border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 ${className}`}
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-amber-700 dark:text-amber-300 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-heading font-extrabold text-amber-900 dark:text-amber-200">
            You&rsquo;re inside your {state.days_remaining}-day refund window
          </h3>
          <p className="text-xs text-amber-900/80 dark:text-amber-100/90 leading-relaxed mt-1">
            To keep pricing fair, new lessons unlock at a pace of{' '}
            <strong>{state.daily_limit} per day</strong> until your refund
            window ends. Today: <strong>{state.lessons_unlocked_today}/{state.daily_limit} used</strong>{' '}
            ({remaining} remaining). Lessons you&rsquo;ve already opened stay
            unlocked and don&rsquo;t count.
          </p>
          {state.refund_voided && (
            <div
              data-testid="refund-voided-notice"
              className="mt-2 text-xs font-bold text-red-700 dark:text-red-300 flex items-start gap-1.5"
            >
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                You&rsquo;ve completed {state.consumption_pct}% of this course during
                the refund window, so your refund entitlement has been voided
                (threshold is 50%). You still keep full access.
              </span>
            </div>
          )}
          {!state.refund_voided && consumptionCloseToVoid && (
            <p className="mt-2 text-xs font-bold text-amber-800 dark:text-amber-200">
              Heads-up: you&rsquo;re at {state.consumption_pct}% course completion —
              past 50% during the refund window auto-voids the refund.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RefundWindowBanner;
